<script>
import { IconClose } from '@arco-design/web-vue/es/icon'
import ZlButton from '@/components/ZlButton.vue'

export default {
  name: 'ZlDrawer',
  components: {
    IconClose,
    ZlButton
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    width: {
      type: [String, Number],
      default: '400px'
    },
    closable: {
      type: Boolean,
      default: true
    },
    maskClosable: {
      type: Boolean,
      default: true
    },
    showFooter: {
      type: Boolean,
      default: false
    },
    placement: {
      type: String,
      default: 'right',
      validator: (value) => ['left', 'right', 'top', 'bottom'].includes(value)
    },
    onClose: {
      type: Function,
      default: null
    }
  },
  emits: ['update:visible', 'close'],
  data() {
    return {
      isAnimating: false
    }
  },
  computed: {
    drawerWidth() {
      if (typeof this.width === 'number') {
        return `${this.width}px`
      }
      return this.width
    },
    drawerStyle() {
      const style = {}
      if (this.placement === 'right' || this.placement === 'left') {
        style.width = this.drawerWidth
        style.height = '100%'
      } else {
        style.height = this.drawerWidth
        style.width = '100%'
      }
      return style
    },
    placementText() {
      const map = {
        'right': '右侧',
        'left': '左侧',
        'top': '顶部',
        'bottom': '底部'
      }
      return map[this.placement] || '右侧'
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.isAnimating = true
        this.$nextTick(() => {
          document.body.style.overflow = 'hidden'
        })
      } else {
        document.body.style.overflow = ''
        setTimeout(() => {
          this.isAnimating = false
        }, 300)
      }
    }
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeydown)
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown)
    document.body.style.overflow = ''
  },
  methods: {
    // 处理遮罩点击
    handleMaskClick(e) {
      if (this.maskClosable && e.target === e.currentTarget) {
        this.handleClose()
      }
    },

    // 处理关闭
    handleClose() {
      this.$emit('update:visible', false)
      this.$emit('close')
      if (this.onClose) {
        this.onClose()
      }
    },

    // 键盘事件处理
    handleKeydown(e) {
      if (e.key === 'Escape' && this.closable) {
        this.handleClose()
      }
    }
  }
}
</script>

<template>
  <teleport to="body">
    <transition name="drawer-fade">
      <div
        v-if="visible || isAnimating"
        class="zl-drawer-mask"
        @click="handleMaskClick"
      >
        <transition :name="`drawer-slide-${placement}`">
          <div
            v-if="visible"
            class="zl-drawer"
            :class="`zl-drawer-${placement}`"
            :style="drawerStyle"
            @click.stop
          >
            <!-- 抽屉头部 -->
            <div v-if="title || closable" class="zl-drawer-header">
              <div class="zl-drawer-title">{{ title }}</div>
              <button
                v-if="closable"
                class="zl-drawer-close"
                @click="handleClose"
              >
                <IconClose />
              </button>
            </div>

            <!-- 抽屉内容 -->
            <div class="zl-drawer-body">
              <slot>
                <!-- 默认内容 -->
                <div class="zl-drawer-default-content">
                  <h3>{{ title || '抽屉内容' }}</h3>
                  <p>这是一个具有毛玻璃效果的抽屉组件，从{{ placementText }}滑出。</p>
                  <p>您可以在这里放置任何内容：</p>
                  <ul>
                    <li>表单组件</li>
                    <li>详情信息</li>
                    <li>操作面板</li>
                    <li>设置选项</li>
                  </ul>
                  <div class="demo-actions">
                    <ZlButton variant="primary" @click="handleClose">关闭抽屉</ZlButton>
                  </div>
                </div>
              </slot>
            </div>

            <!-- 抽屉底部 -->
            <div v-if="showFooter" class="zl-drawer-footer">
              <slot name="footer"></slot>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </teleport>
</template>

<style scoped>
.zl-drawer-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  
  /* 毛玻璃背景 */
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.zl-drawer {
  position: absolute;
  display: flex;
  flex-direction: column;
  
  /* 毛玻璃效果 */
  background: rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(25px) saturate(200%);
  -webkit-backdrop-filter: blur(25px) saturate(200%);
  border: 1px solid rgba(255, 255, 255, 0.3);
  
  /* 阴影效果 */
  box-shadow: 
    0 25px 80px rgba(0, 0, 0, 0.2),
    0 15px 50px rgba(0, 0, 0, 0.15),
    0 8px 25px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.4);
  
  overflow: hidden;
  
  /* 添加微妙的动画过渡 */
  transition: all 0.2s ease;
}

/* 抽屉悬停效果 */
.zl-drawer:hover {
  box-shadow: 
    0 30px 90px rgba(0, 0, 0, 0.25),
    0 20px 60px rgba(0, 0, 0, 0.18),
    0 10px 30px rgba(0, 0, 0, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
  
  backdrop-filter: blur(28px) saturate(220%);
  -webkit-backdrop-filter: blur(28px) saturate(220%);
}

/* 不同位置的抽屉样式 */
.zl-drawer-right {
  top: 0;
  right: 0;
  border-left: 1px solid rgba(255, 255, 255, 0.3);
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
}

.zl-drawer-left {
  top: 0;
  left: 0;
  border-right: 1px solid rgba(255, 255, 255, 0.3);
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
}

.zl-drawer-top {
  top: 0;
  left: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
}

.zl-drawer-bottom {
  bottom: 0;
  left: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.3);
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.zl-drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  flex-shrink: 0;
}

.zl-drawer-title {
  font-size: 18px;
  font-weight: 600;
  color: #000000;
  margin: 0;
}

.zl-drawer-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  color: #000000;
  transition: all 0.3s cubic-bezier(0.23, 1, 0.32, 1);
  font-size: 16px;
}

.zl-drawer-close:hover {
  background: rgba(255, 255, 255, 0.3);
  color: #000000;
  transform: rotate(90deg) scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.zl-drawer-close:active {
  transform: rotate(90deg) scale(0.95);
}

.zl-drawer-body {
  flex: 1;
  padding: 20px 24px;
  overflow-y: auto;
  color: #000000;
  line-height: 1.6;
}

.zl-drawer-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  flex-shrink: 0;
}

/* 默认内容样式 */
.zl-drawer-default-content {
  padding: 0;
}

.zl-drawer-default-content h3 {
  color: #000000;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
}

.zl-drawer-default-content p {
  color: #000000;
  line-height: 1.6;
  margin-bottom: 12px;
}

.zl-drawer-default-content ul {
  color: #000000;
  margin: 16px 0;
  padding-left: 20px;
}

.zl-drawer-default-content li {
  margin-bottom: 8px;
  line-height: 1.5;
}

.demo-actions {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
}

/* 遮罩动画 - 增强版 */
.drawer-fade-enter-active {
  transition: all 0.5s cubic-bezier(0.23, 1, 0.32, 1);
}

.drawer-fade-leave-active {
  transition: all 0.4s cubic-bezier(0.755, 0.05, 0.855, 0.06);
}

.drawer-fade-enter-from,
.drawer-fade-leave-to {
  opacity: 0;
  backdrop-filter: blur(0px);
  -webkit-backdrop-filter: blur(0px);
}

/* 右侧滑入动画 - 增强版 */
.drawer-slide-right-enter-active {
  transition: all 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  transform-origin: left center;
}

.drawer-slide-right-leave-active {
  transition: all 0.4s cubic-bezier(0.755, 0.05, 0.855, 0.06);
  transform-origin: left center;
}

.drawer-slide-right-enter-from {
  transform: translateX(100%);
  opacity: 0;
  box-shadow: none;
}

.drawer-slide-right-leave-to {
  transform: translateX(100%);
  opacity: 0;
  box-shadow: none;
}

.drawer-slide-right-enter-to {
  transform: translateX(0);
  opacity: 1;
}

/* 左侧滑入动画 - 增强版 */
.drawer-slide-left-enter-active {
  transition: all 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  transform-origin: right center;
}

.drawer-slide-left-leave-active {
  transition: all 0.4s cubic-bezier(0.755, 0.05, 0.855, 0.06);
  transform-origin: right center;
}

.drawer-slide-left-enter-from {
  transform: translateX(-100%);
  opacity: 0;
  box-shadow: none;
}

.drawer-slide-left-leave-to {
  transform: translateX(-100%);
  opacity: 0;
  box-shadow: none;
}

.drawer-slide-left-enter-to {
  transform: translateX(0);
  opacity: 1;
}

/* 顶部滑入动画 - 增强版 */
.drawer-slide-top-enter-active {
  transition: all 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  transform-origin: center bottom;
}

.drawer-slide-top-leave-active {
  transition: all 0.4s cubic-bezier(0.755, 0.05, 0.855, 0.06);
  transform-origin: center bottom;
}

.drawer-slide-top-enter-from {
  transform: translateY(-100%);
  opacity: 0;
  box-shadow: none;
}

.drawer-slide-top-leave-to {
  transform: translateY(-100%);
  opacity: 0;
  box-shadow: none;
}

.drawer-slide-top-enter-to {
  transform: translateY(0);
  opacity: 1;
}

/* 底部滑入动画 - 增强版 */
.drawer-slide-bottom-enter-active {
  transition: all 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  transform-origin: center top;
}

.drawer-slide-bottom-leave-active {
  transition: all 0.4s cubic-bezier(0.755, 0.05, 0.855, 0.06);
  transform-origin: center top;
}

.drawer-slide-bottom-enter-from {
  transform: translateY(100%);
  opacity: 0;
  box-shadow: none;
}

.drawer-slide-bottom-leave-to {
  transform: translateY(100%);
  opacity: 0;
  box-shadow: none;
}

.drawer-slide-bottom-enter-to {
  transform: translateY(0);
  opacity: 1;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .zl-drawer-right,
  .zl-drawer-left {
    width: 90vw !important;
    max-width: 350px;
  }
  
  .zl-drawer-top,
  .zl-drawer-bottom {
    height: 70vh !important;
    max-height: 500px;
  }
  
  .zl-drawer-header {
    padding: 16px 20px 12px;
  }
  
  .zl-drawer-title {
    font-size: 16px;
  }
  
  .zl-drawer-body {
    padding: 16px 20px;
  }
  
  .zl-drawer-footer {
    padding: 12px 20px 16px;
    flex-direction: column-reverse;
  }
  
  .zl-drawer-footer .arco-btn {
    width: 100%;
  }
}

/* 滚动条样式 */
.zl-drawer-body::-webkit-scrollbar {
  width: 6px;
}

.zl-drawer-body::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

.zl-drawer-body::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
  transition: background 0.3s ease;
}

.zl-drawer-body::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}
</style>